import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  'Prévention & Droits',
  'Santé & Bien-être',
  'Famille & Éducation',
  'Compétences & Travail',
  'Culture & Société',
  'Aide & Support Kurso',
];
const POST_TYPES = [
  { value: 'question', label: 'Question' },
  { value: 'temoignage', label: 'Témoignage' },
  { value: 'ressource', label: 'Ressource' },
  { value: 'idee', label: 'Idée' },
];

export default function NewForumPost() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: '',
    content: '',
    category: CATEGORIES[0],
    post_type: 'question',
    tags: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.title.length < 5) { toast.error('Titre trop court (min. 5 caractères)'); return; }
    if (form.content.length < 10) { toast.error('Contenu trop court (min. 10 caractères)'); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
      };
      const r = await axios.post(`${API}/api/forum/posts`, payload, { withCredentials: true });
      toast.success('Discussion créée ! +10 XP');
      navigate(`/communaute/${r.data.post_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la création');
    } finally { setSubmitting(false); }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-6">
        <button
          onClick={() => navigate('/communaute')}
          className="flex items-center gap-2 text-[#a0a0ab] hover:text-white transition-colors text-sm mb-6"
        >
          <ArrowLeft size={16} /> Communauté
        </button>

        <div className="mb-6">
          <h1 className="text-2xl font-heading font-bold text-white">Nouvelle discussion</h1>
          <p className="text-[#a0a0ab] text-sm mt-1">Partagez une question, une ressource ou un témoignage — gagnez +10 XP</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Type */}
          <div>
            <label className="text-xs text-[#a0a0ab] mb-2 block">Type de publication</label>
            <div className="flex gap-2 flex-wrap">
              {POST_TYPES.map(type => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, post_type: type.value }))}
                  data-testid={`post-type-${type.value}`}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                    form.post_type === type.value
                      ? 'bg-[#ff6b6b] text-white border-[#ff6b6b]'
                      : 'bg-white/5 text-[#e8e8e8] border-white/10 hover:bg-white/10'
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="text-xs text-[#a0a0ab] mb-1.5 block">Titre *</label>
            <input
              type="text"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="Résumez votre sujet en une phrase..."
              maxLength={200}
              required
              className="w-full bg-white/5 border border-white/10 text-white placeholder-[#a0a0ab] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#ff6b6b]/50"
              data-testid="post-title-input"
            />
            <p className="text-xs text-[#a0a0ab] text-right mt-1">{form.title.length}/200</p>
          </div>

          {/* Content */}
          <div>
            <label className="text-xs text-[#a0a0ab] mb-1.5 block">Contenu *</label>
            <textarea
              value={form.content}
              onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
              placeholder="Développez votre question, partagez votre expérience..."
              rows={6}
              required
              minLength={10}
              className="w-full bg-white/5 border border-white/10 text-white placeholder-[#a0a0ab] rounded-xl px-4 py-3 text-sm resize-none focus:outline-none focus:border-[#ff6b6b]/50"
              data-testid="post-content-input"
            />
          </div>

          {/* Category */}
          <div>
            <label className="text-xs text-[#a0a0ab] mb-1.5 block">Catégorie *</label>
            <select
              value={form.category}
              onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
              className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#ff6b6b]/50"
              data-testid="post-category-select"
            >
              {CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          {/* Tags */}
          <div>
            <label className="text-xs text-[#a0a0ab] mb-1.5 block">Tags (optionnel, séparés par virgules)</label>
            <input
              type="text"
              value={form.tags}
              onChange={e => setForm(f => ({ ...f, tags: e.target.value }))}
              placeholder="rgpd, santé, famille..."
              className="w-full bg-white/5 border border-white/10 text-white placeholder-[#a0a0ab] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#ff6b6b]/50"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => navigate('/communaute')}
              className="flex-1 bg-white/5 hover:bg-white/10 text-white py-3 rounded-xl text-sm border border-white/10 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={submitting || form.title.length < 5 || form.content.length < 10}
              className="flex-1 btn-primary py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="submit-post-btn"
            >
              <Send size={14} />
              {submitting ? 'Publication...' : 'Publier la discussion'}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
